import type {JobsData, AgencyList} from 'src/types/referrer-portal';
import type {Agency} from 'src/api-parsers/index.types';
import * as React from 'react';
import useAgencyConfig from 'src/hooks/useAgencyConfig';
import {useReferralPayoutEnabled} from 'src/hooks/useReferrals';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useInflight} from 'src/hooks/redux';
import {useDispatch, useSelector} from 'react-redux';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {selectAgencyList, selectJobs} from 'src/selectors/referrer-portal';
import {JOB_LIST_DATA_LIMIT} from 'src/utils/referral-v2';
import classify from 'src/utils/classify';
import {duotoneIconStyle} from 'src/utils/app-shell';
import {
  isReferrerPortal,
  isSubstringPresent,
  getTableBorderRadius,
} from 'src/components/referral-v2/common';
import {getProgramJobs} from 'src/action-creators/referrer-portal/user-form';
import {getAllReferralsPrograms} from 'src/action-creators/referrer-portal/tracking';
import {selectAgencyData} from 'src/selectors/agency';
import {getHeaders} from 'src/components/referrer-portal/jobs/jobs-table-header';
import {
  SearchInput,
  Table,
  TableTopBar,
  TableBottomBar,
  EmptyState,
  PageTitle,
  Pagination,
  SubTitleExtraSmall,
  PageName,
  TitleMedium,
  Icon,
} from '@spaced-out/ui-design-system/lib/components/index';

import {AgencyAndProgramFilter} from 'src/components/referrer-portal/my-referrals/filters';
import tableCss from 'src/components/referral-v2/tracking/tracking.css';
import css from 'src/components/referrer-portal/jobs/jobs.css';


const recordsPerPage = 10;

const Jobs = () => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();

  const agencyList: AgencyList[] | null | undefined =
    useSelector(selectAgencyList);

  const referralPayoutEnabled = useReferralPayoutEnabled();
  const loggedInAgencyData: Agency | null | undefined =
    useSelector(selectAgencyData);

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AgencyConfig'.
  const showRefIdColumn: boolean =
    useAgencyConfig('referralJobReferenceIdEnabled') ?? false;

  const defaultAgencySelected =
    Array.isArray(agencyList) && agencyList.length > 0
      ? agencyList[0].id
      : loggedInAgencyData?.id ?? '';

  const [searchText, setSearchText] = React.useState('');
  const [agencyId, setAgencyId] = React.useState(defaultAgencySelected);
  const [programId, setProgramId] = React.useState('');
  const [currentPageJobs, setCurrentPageJobs] = React.useState([]);
  const [page, setPage] = React.useState(1);

  // @ts-expect-error - TS2322 - Type 'JobResponse[]' is not assignable to type 'JobsData[]'.
  const totalJobs: JobsData[] = useSelector(selectJobs) || [];

  const getJobs = (value: string, agencyId: string, programId: string) => {
    if (!!agencyId && programId) {
      const filtersPayload = {
        search_str: value,
        offset: 0,
        limit: JOB_LIST_DATA_LIMIT,
        location: '',
      } as const;

      return dispatch(getProgramJobs(agencyId, programId, filtersPayload));
    }
    return Promise.resolve();
  };

  const isProgramLoading = useInflight(getAllReferralsPrograms(agencyId));

  const filteredJob = React.useMemo(() => {
    const isQueryMatched = (job: JobsData) =>
      isSubstringPresent(searchText, job.title) ||
      isSubstringPresent(searchText, job.location) ||
      isSubstringPresent(searchText, job.job_reference_id);

    return totalJobs.filter(
      (job) => job.id && (!searchText || isQueryMatched(job)),
    );
  }, [searchText, totalJobs]);

  const totalPages = filteredJob
    ? Math.ceil(filteredJob.length / recordsPerPage)
    : 0;

  const isJobsLoading = useAsyncDependency(
    () => getJobs(searchText, agencyId, programId),
    [programId],
  );

  const isReferrerPortalEnv = isReferrerPortal();

  const onAgencyAndProgramFilterChange = (
    filterName: string,
    value: string | null | undefined,
  ) => {
    setPage(1);
    if (filterName === 'agency_id') {
      setAgencyId(value ?? '');
    } else {
      setProgramId(value ?? '');
    }
  };

  // @ts-expect-error - TS7006 - Parameter 'searchText' implicitly has an 'any' type.
  const handleChangeSearchText = React.useCallback((searchText) => {
    setPage(1);
    setSearchText(searchText);
  }, []);

  React.useEffect(() => {
    const offset = (page - 1) * recordsPerPage;
    if (filteredJob.length > 0) {
      // @ts-expect-error - TS2345 - Argument of type 'JobsData[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setCurrentPageJobs(filteredJob.slice(offset, offset + recordsPerPage));
    }
  }, [page, totalJobs, searchText]);

  return (
    <div className={css.jobPage}>
      {isReferrerPortalEnv && (
        <PageTitle>
          <PageName>
            <TitleMedium>{t('Jobs', 'Jobs')}</TitleMedium>
            <Icon type="duotone" name="briefcase" style={duotoneIconStyle} />
          </PageName>
        </PageTitle>
      )}
      <div
        className={classify({
          [tableCss.referralPortaltrackingContainer]: isReferrerPortalEnv,
          [tableCss.trackingContainer]: !isReferrerPortalEnv,
        })}
      >
        <TableTopBar>
          <SearchInput
            onClear={() => {
              setSearchText('');
            }}
            value={searchText}
            placeholder={t(
              'Search_by_job_title_or_location',
              'Search by job title or location',
            )}
            classNames={{wrapper: tableCss.trackingSearch}}
            onChange={(event) => handleChangeSearchText(event.target.value)}
          />
          <AgencyAndProgramFilter
            agencyId={agencyId}
            programId={programId}
            onChangeFilter={onAgencyAndProgramFilterChange}
            required={true}
          />
        </TableTopBar>
        <Table
          borderRadius={getTableBorderRadius(totalPages)}
          entries={currentPageJobs}
          stickyHeader={true}
          headers={getHeaders(t, showRefIdColumn, referralPayoutEnabled)}
          isLoading={isJobsLoading || isProgramLoading}
          classNames={{
            wrapper: css.tableWrapper,
          }}
          emptyText={
            <EmptyState
              description={
                searchText
                  ? `${t(
                      'Doublecheck_your_spelling_or_try_using_different_keywords',
                      'Double-check your spelling or try using different keywords',
                    )}.`
                  : `${t(
                      'Once_the_jobs_are_available_the_list_will_be_displayed_here',
                      'Once the jobs are available, the list will be displayed here',
                    )}.`
              }
              imageVariant="data"
              title={
                searchText
                  ? t('No_results_found', 'No results found')
                  : t('No_jobs_available', 'No jobs available')
              }
              classNames={{wrapper: tableCss.emptyTableContainer}}
            />
          }
          idName="id"
          extras={{programId, agencyId}}
        />
        {!(isJobsLoading || isProgramLoading) && totalPages > 1 && (
          <TableBottomBar>
            <Pagination
              currentPage={page}
              onChange={(page) => setPage(page || 0)}
              totalPages={totalPages}
            >
              <SubTitleExtraSmall color="secondary">
                {`${t('Showing_page', 'Showing page')} `}
                {page}
                {` ${t('of', 'of')} `}
                {totalPages}
              </SubTitleExtraSmall>
            </Pagination>
          </TableBottomBar>
        )}
      </div>
    </div>
  );
};

export default Jobs;
